import type { Http2ServerRequest } from 'node:http2'

import { type Sso, SsoType } from '@bettermode/common/gql/generated'
import { DomainPathInfo } from '@tribeplatform/react-components/common/utils'

import { RuntimeConfigs } from '../../../../../configs/runtime.js'
import { SsoStateType } from '../../../types/server.types.js'
import {
  SSO_CALLBACK_PATH,
  SSO_FORWARDER_PATH,
  SSO_JWT_CALLBACK_PATH,
  SSO_SAML_CALLBACK_PATH,
} from '../constants/ssos.constants.js'

export const rewriteToSsoCallbackUrl = (requestUrl: string): string => {
  if (requestUrl.startsWith(SSO_FORWARDER_PATH)) {
    return requestUrl.replace(SSO_FORWARDER_PATH, SSO_CALLBACK_PATH)
  }

  return `${SSO_CALLBACK_PATH}${requestUrl}`
}

export const getSsoCallbackUrl = (
  type: SsoType,
  getResourceUrl: DomainPathInfo['getResourceUrl'],
  redirect?: string,
): string => {
  const sharedSsoCallbackPath = `${RuntimeConfigs.SSOS_FORWARDER_SITE}${SSO_FORWARDER_PATH}`
  const sharedSsoCallbackUrl = `${sharedSsoCallbackPath}?redirect_uri=${
    redirect || '/'
  }`

  switch (type) {
    case SsoType.jwt:
      return getResourceUrl(SSO_JWT_CALLBACK_PATH)

    case SsoType.Saml:
      return getResourceUrl(SSO_SAML_CALLBACK_PATH)

    case SsoType.oauth2:
      return getResourceUrl(
        `${SSO_CALLBACK_PATH}${redirect ? `?redirect_uri=${redirect}` : ''}`,
      )

    case SsoType.Apple:
    case SsoType.google:
    case SsoType.facebook:
    case SsoType.linkedin:
    case SsoType.Discord:
    case SsoType.Slack:
      return sharedSsoCallbackUrl

    default: {
      const _exhaustiveCheck: SsoType.jwt = type
      return sharedSsoCallbackUrl
    }
  }
}

const CUSTOM_SSO_PRIORITIES = [SsoType.oauth2, SsoType.jwt, SsoType.Saml]
export const sortSsosOptions = <T extends Array<{ type: Sso['type'] }>>(
  ssos: T,
): T[number][] => {
  return [...ssos].sort((a, b) => {
    const aIndex = CUSTOM_SSO_PRIORITIES.indexOf(a.type)
    const bIndex = CUSTOM_SSO_PRIORITIES.indexOf(b.type)

    if (aIndex === -1 && bIndex === -1) {
      return 0
    }

    if (aIndex === -1) {
      return 1
    }

    if (bIndex === -1) {
      return -1
    }

    return aIndex - bIndex
  })
}

export const isCustomSso = (sso: { type: Sso['type'] }): boolean => {
  return CUSTOM_SSO_PRIORITIES.includes(sso.type)
}

export const isFunctioningSsoOption = (sso: {
  type: Sso['type']
  authorizationUrl: Sso['authorizationUrl']
  signUpUrl: Sso['signUpUrl']
  tokenUrl: Sso['tokenUrl']
}): boolean => {
  if (sso.type === SsoType.jwt) {
    return !!sso.authorizationUrl || !!sso.signUpUrl
  }

  if (sso.type === SsoType.Saml) {
    /**
     * Apparently we haven't prevented customers from shooting themselves in the foot
     * and they can enable SAML without a tokenUrl (?)
     */
    return !!sso.tokenUrl
  }

  return true
}

export const isVisibleSsoOption = (sso: {
  type: Sso['type']
  buttonText: Sso['buttonText']
}): boolean => {
  if (isCustomSso(sso)) {
    /**
     * Because JWT is a special snowflake with hardcoded fallback text!
     */
    if (sso.type === SsoType.jwt) {
      return true
    }

    return !!sso.buttonText
  }

  return true
}

export enum AuthOperationType {
  'login' = 'login',
  'signup' = 'signup',
}

export const getAuthOperationType = (
  apiPath: Http2ServerRequest['path'],
): AuthOperationType => {
  if (apiPath.endsWith('/auth/signup')) {
    return AuthOperationType.signup
  }
  return AuthOperationType.login
}

export const parseSsoState = (state: string): Partial<SsoStateType> | null => {
  if (!state) {
    return null
  }

  try {
    const decodedState = Buffer.from(state, 'base64').toString('utf-8')
    const uriDecodedState = decodeURIComponent(decodedState)
    const parsedState = JSON.parse(uriDecodedState) as SsoStateType
    const ssoNetworkDomain = parsedState?.networkDomain

    if (!ssoNetworkDomain) {
      return null
    }

    return parsedState
  } catch (error) {
    return null
  }
}

export type OAuth2CallbackParams = {
  idpResponse: {
    state: string
    code: string
    hd: string
    prompt: string
    scope: string
    oauthToken: string
    oauthVerifier: string
  }
  relayHints: {
    networkDomain: string
    redirectUri: string
  }
}

export const extractOAuthCallbackParams = (
  data: URLSearchParams | unknown,
): OAuth2CallbackParams => {
  const isSearchParam = data instanceof URLSearchParams
  const params = isSearchParam ? data : new Map(Object.entries(data || {}))
  const state = params.get('state') ?? ''
  const parsedState = parseSsoState(state)

  return {
    idpResponse: {
      state,
      code: params.get('code') ?? '',
      hd: params.get('hd') ?? '',
      prompt: params.get('prompt') ?? '',
      scope: params.get('scope') ?? '',
      oauthToken: params.get('oauth_token') ?? '',
      oauthVerifier: params.get('oauth_verifier') ?? '',
    },
    relayHints: {
      networkDomain: parsedState?.networkDomain,
      redirectUri:
        parsedState?.redirectUri ?? params.get('redirect_uri') ?? '/',
    },
  }
}
